import { WHISPER_CONFIG } from '../../config/whisper';
import {
  CaptureSource,
  CaptureSourceCallbacks,
  ContainerCutter,
} from './types';

/**
 * Timeslice for the continuous recorder. 1000 ms is the timeslice already in
 * production; the spike verified one WebM Cluster per timeslice at this value,
 * which is what makes fragment count a duration proxy for cut placement.
 */
export const CONTINUOUS_TIMESLICE_MS = 1000;

/**
 * How long a muted microphone track is tolerated before the capture is treated
 * as failed. A track goes `muted` whenever the OS stops handing over samples —
 * commonly a brief contention with another application, which recovers on its
 * own — so escalating instantly would kill visits that were never broken. Past
 * this window there is no audio being captured and the user has to be told.
 */
export const MIC_MUTE_GRACE_MS = 5000;

/**
 * How long past its target length a chunk is overdue. Chromium's count cut
 * lands at most one timeslice past the target, a ~1–2 s margin before this
 * fires. If it does fire there, it cuts at the last complete Cluster, so no
 * audio is lost.
 */
export const FORCED_CUT_GRACE_MS = 2000;

/**
 * NEVER set this to 30 s or more: a chunk over 30 s comes back from Whisper
 * with no text and is stored as an empty transcript.
 *
 * Hard cap on the overdue deadline. The Whisper GPU worker fails on audio
 * longer than 30.000 s (transformers 4.41.1's long-form branch with word
 * timestamps throws `'NoneType' object is not subscriptable`), so a forced cut
 * must land well before 30 s. A target of 28 s or more (the chunk length may
 * be set up to 30 s) plus FORCED_CUT_GRACE_MS would otherwise reach 30 s;
 * #1192's chunk slots up to 24 s stay under the cap.
 */
export const FORCED_CUT_MAX_MS = 27_000;

/** Buffered size that forces a cut, so no chunk reaches the server's 1 MiB limit. */
export const FORCED_CUT_TRIGGER_BYTES = Math.floor(
  (WHISPER_CONFIG.maxChunkSizeBytes * 3) / 4
);

/**
 * Strategy B: ONE MediaRecorder runs for the whole capture and its byte stream
 * is cut into self-contained chunks at real container boundaries by a
 * ContainerCutter. The recorder is never stopped mid-take, so the per-seam
 * capture loss of the legacy rotate-per-chunk scheme (~145 ms every 12 s) does
 * not exist here, and no audio is duplicated.
 */
export class ContinuousCutSource implements CaptureSource {
  private recorder: MediaRecorder | null = null;
  /** The stream of the latest start(), which a forced restart records on again. */
  private stream: MediaStream | null = null;
  /** Wall-clock ms of the last emitted chunk, or of the run's start. */
  private lastEmitMs = 0;
  /**
   * Serializes every cutter interaction: blob→bytes reads resolve async, so
   * appends, the stop flush, and the reset for a new run are chained to keep
   * byte order identical to delivery order.
   */
  private ingestChain: Promise<void> = Promise.resolve();
  /**
   * The previous run's stop, on which the current run's every ingest enqueue
   * (reset, appends, stop flush) is gated. The previous run's final
   * dataavailable append and flush are enqueued asynchronously from event
   * handlers, so a fast pause→resume could otherwise order the reset ahead of
   * them and wipe or misparse the pre-pause tail; the gate makes
   * [final-append][flush][reset][new appends] structural, not timing-dependent.
   */
  private runGate: Promise<void> = Promise.resolve();
  /**
   * Memoized in-flight (or last) stop. Every concurrent stop() awaits the same
   * tail-inclusive completion — the CaptureSource idempotency contract; a
   * second stop resolving early would let the caller mark ?final=true before
   * the tail chunk is numbered.
   */
  private stopPromise: Promise<void> | null = null;
  /** Listener teardown for the tracks of the CURRENT run's stream. */
  private trackWatchers: Array<() => void> = [];
  private muteTimer: number | null = null;
  /** When the pending muteTimer fails the capture; read only while it is set. */
  private muteDeadlineMs = 0;
  /** One report per run: a dead device raises several events in a row. */
  private captureFailed = false;
  /**
   * Fires the overdue check on our clock, so a browser that delivers no data
   * (or holds it far past the timeslice) is still cut. Armed for the live run
   * at its overdue deadline from lastEmitMs.
   */
  private overdueTimer: number | null = null;
  private readonly fragmentsPerChunk: number;
  /** min(target + FORCED_CUT_GRACE_MS, FORCED_CUT_MAX_MS) from the last emit. */
  private readonly overdueAfterMs: number;

  constructor(
    private readonly mimeType: string,
    private readonly cutter: ContainerCutter,
    private readonly callbacks: CaptureSourceCallbacks
  ) {
    this.fragmentsPerChunk = Math.max(
      1,
      Math.round(WHISPER_CONFIG.chunkDurationMs / CONTINUOUS_TIMESLICE_MS)
    );
    this.overdueAfterMs = Math.min(
      this.fragmentsPerChunk * CONTINUOUS_TIMESLICE_MS + FORCED_CUT_GRACE_MS,
      FORCED_CUT_MAX_MS
    );
  }

  start(stream: MediaStream): void {
    if (this.recorder) {
      throw new Error('ContinuousCutSource is already capturing');
    }
    // Rejection-proofed: a stop that failed still gates (and its error already
    // surfaced to that stop's caller) — the new run must reset and proceed.
    const gate = (this.stopPromise ?? Promise.resolve()).catch(() => undefined);
    this.runGate = gate;
    this.stopPromise = null;
    void gate.then(() => {
      void this.enqueueIngest(() => {
        this.cutter.reset();
      });
    });

    const recorder = new MediaRecorder(stream, {
      mimeType: this.mimeType,
      audioBitsPerSecond: WHISPER_CONFIG.audioConfig.audioBitsPerSecond,
    });

    recorder.ondataavailable = (event: BlobEvent) => {
      if (!event.data || event.data.size === 0) {
        return;
      }
      // Gated behind the same promise as the reset above; .then callbacks run
      // in registration order, so appends land after the reset in the chain.
      void gate.then(() => {
        void this.enqueueIngest(async () => {
          const bytes = new Uint8Array(await event.data.arrayBuffer());
          this.cutter.append(bytes);
          this.emitReadyChunks(recorder);
        });
      });
    };
    recorder.onerror = (event) => {
      const error = (event as unknown as { error?: unknown }).error;
      this.callbacks.onError(
        error instanceof Error
          ? error
          : new Error('MediaRecorder error during continuous capture')
      );
    };

    recorder.start(CONTINUOUS_TIMESLICE_MS);
    this.lastEmitMs = Date.now();
    this.recorder = recorder;
    this.stream = stream;
    this.armOverdueTimer();
    this.captureFailed = false;
    this.watchTracks(stream);
  }

  stop(): Promise<void> {
    const recorder = this.recorder;
    if (!recorder) {
      return this.stopPromise ?? Promise.resolve();
    }
    this.recorder = null;
    this.clearOverdueTimer();
    this.unwatchTracks();
    const gate = this.runGate;

    this.stopPromise = (async () => {
      await new Promise<void>((resolve) => {
        if (recorder.state === 'inactive') {
          resolve();
          return;
        }
        recorder.onstop = () => resolve();
        recorder.stop();
      });

      // The recorder's final dataavailable fired before onstop, so its
      // (equally gated) ingest is registered ahead of this flush.
      await gate;
      await this.enqueueIngest(() => {
        const tail = this.cutter.flush();
        if (tail && tail.length > 0) {
          this.emit(tail);
        }
      });
    })();
    return this.stopPromise;
  }

  async switchStream(stream: MediaStream): Promise<void> {
    await this.stop();
    this.start(stream);
  }

  /**
   * Microphone loss is surfaced ONLY from here on a continuous run: the source
   * never re-arms per slice, so the legacy consecutive-arm-failure escalation
   * (which lives in the rotating recorder's catch) can never fire, and a dead
   * microphone would otherwise look exactly like silence.
   *
   * `ended` is fatal immediately — a MediaStreamTrack that has ended cannot be
   * restarted, so the device is gone. `mute` is given MIC_MUTE_GRACE_MS to
   * recover first: the OS mutes a track for ordinary, self-healing contention.
   */
  private watchTracks(stream: MediaStream): void {
    for (const track of stream.getAudioTracks()) {
      const label = track.label || 'unknown';
      const onEnded = () =>
        this.failCapture(
          `Microphone "${label}" is no longer available; recording stopped`
        );
      const onMute = () => {
        if (this.muteTimer !== null) return;
        this.armMuteTimer(label, MIC_MUTE_GRACE_MS);
      };
      const onUnmute = () => this.clearMuteTimer();
      track.addEventListener('ended', onEnded);
      track.addEventListener('mute', onMute);
      track.addEventListener('unmute', onUnmute);
      this.trackWatchers.push(() => {
        track.removeEventListener('ended', onEnded);
        track.removeEventListener('mute', onMute);
        track.removeEventListener('unmute', onUnmute);
      });
    }
  }

  private armMuteTimer(label: string, delayMs: number): void {
    this.muteDeadlineMs = Date.now() + delayMs;
    this.muteTimer = window.setTimeout(() => {
      this.muteTimer = null;
      this.failCapture(
        `Microphone "${label}" stopped delivering audio; recording stopped`
      );
    }, delayMs);
  }

  private unwatchTracks(): void {
    this.clearMuteTimer();
    for (const teardown of this.trackWatchers) {
      teardown();
    }
    this.trackWatchers = [];
  }

  private clearMuteTimer(): void {
    if (this.muteTimer !== null) {
      clearTimeout(this.muteTimer);
      this.muteTimer = null;
    }
  }

  private failCapture(message: string): void {
    if (this.captureFailed) return;
    this.captureFailed = true;
    this.unwatchTracks();
    this.callbacks.onError(new Error(message));
  }

  private enqueueIngest(task: () => void | Promise<void>): Promise<void> {
    const next = this.ingestChain.then(task);
    this.ingestChain = next.catch((error) => {
      this.callbacks.onError(
        error instanceof Error ? error : new Error(String(error))
      );
    });
    return this.ingestChain;
  }

  /**
   * The count cut: once the target number of fragments is complete, emit as
   * many of them as fit the upload limit (NEB-103857: a burst of large
   * fragments must not go out in one oversized chunk). When none fits,
   * forceCutIfDue restarts the recorder: one fragment over the limit is
   * already past the size trigger.
   */
  private emitReadyChunks(run: MediaRecorder): void {
    while (this.cutter.completeFragmentCount() >= this.fragmentsPerChunk) {
      const bytes = this.cutter.cutWithin(
        WHISPER_CONFIG.maxChunkSizeBytes,
        this.fragmentsPerChunk
      );
      if (!bytes) {
        break;
      }
      this.emit(bytes);
    }
    this.forceCutIfDue(run);
  }

  private emit(bytes: Uint8Array): void {
    this.lastEmitMs = Date.now();
    this.armOverdueTimer();
    this.callbacks.onChunk({
      blob: new Blob([bytes], { type: this.mimeType }),
      captureEndMs: Date.now(),
    });
  }

  /**
   * NEB-103857: per the MediaRecorder spec the timeslice is a minimum, so a
   * browser can deliver Clusters less often than once per timeslice and the
   * count cut alone can hold a chunk far past its length and past the upload
   * limit. This cuts on our clock and caps the size, at whole Clusters only;
   * with no complete Cluster it restarts the recorder. Runs after every append
   * and from the overdue timer, which covers a browser that delivers nothing
   * until stopped. Each pass emits at least one fragment or returns. Skipped
   * for any run but the live one (stopped, or retired by a restart): that
   * run's stop flush emits its tail.
   */
  private forceCutIfDue(run: MediaRecorder): void {
    if (this.recorder !== run) {
      return;
    }
    while (
      Date.now() - this.lastEmitMs >= this.overdueAfterMs ||
      this.cutter.bufferedByteLength() >= FORCED_CUT_TRIGGER_BYTES
    ) {
      const bytes = this.cutter.cutWithin(WHISPER_CONFIG.maxChunkSizeBytes);
      if (!bytes) {
        this.restartRecorder();
        return;
      }
      this.emit(bytes);
    }
  }

  /**
   * Arm the overdue check for the live run. It is enqueued behind that run's
   * gate and appends, like an append, so it sees bytes in delivery order.
   */
  private armOverdueTimer(): void {
    this.clearOverdueTimer();
    const run = this.recorder;
    if (!run) {
      return;
    }
    const gate = this.runGate;
    this.overdueTimer = window.setTimeout(
      () => {
        this.overdueTimer = null;
        void gate.then(() => {
          void this.enqueueIngest(() => this.forceCutIfDue(run));
        });
      },
      this.lastEmitMs + this.overdueAfterMs - Date.now()
    );
  }

  private clearOverdueTimer(): void {
    if (this.overdueTimer !== null) {
      window.clearTimeout(this.overdueTimer);
      this.overdueTimer = null;
    }
  }

  /**
   * NEB-103857: when the browser closes no Cluster (one never-ending Cluster,
   * or a single Cluster past the size trigger) there is no cut point, and
   * restarting the recorder in place is the only way to get a complete file
   * out. stop() finalizes the old recorder's file and its flush emits it as one
   * chunk while this source is still live; start() gates the new run's reset
   * and appends behind that stop. Both run synchronously, so no outer
   * stop/pause/switch can interleave. The seam loses the stop→start gap.
   * Chromium never gets here: it closes a Cluster every timeslice. A pending
   * mute countdown keeps its deadline: stop() clears it, and a track that is
   * already muted raises no new 'mute' for the new run.
   */
  private restartRecorder(): void {
    const stream = this.stream;
    if (!stream) {
      throw new Error('ContinuousCutSource has no stream to restart on');
    }
    const muteDeadlineMs = this.muteTimer !== null ? this.muteDeadlineMs : null;
    void this.stop();
    this.start(stream);
    const muted = stream.getAudioTracks().find((track) => track.muted);
    if (muteDeadlineMs !== null && muted) {
      this.armMuteTimer(
        muted.label || 'unknown',
        Math.max(0, muteDeadlineMs - Date.now())
      );
    }
  }
}
